import * as React from "react";
import { MenuItem, Select, Typography, makeStyles, Grid } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Status } from "src/common/components";
import { SubmissionStatus, TalkStatus } from "src/common/types";
import { displayDate } from "src/helpers/";

const useStyles = makeStyles((theme: any) => ({
  stats: {
    display: "block",
    listStyle: "none" as "none",
    margin: "9px 0 29px",
    padding: 0
  },
  statsItem: {
    "&:first-child": {
      borderLeft: "none",
      paddingLeft: 0
    },
    "&:last-child": {
      paddingRight: 0
    },
    borderLeft: "1px solid #979797",
    display: "inline-block" as "inline-block",
    lineHeight: "1.6rem",
    [theme.breakpoints.down("sm")]: {
      padding: "0 10px"
    },
    [theme.breakpoints.up("md")]: {
      padding: "0 24px"
    }
  }
}));

interface IProps {
  title: string;
  subTitle?: string;
  template: string;
  dataTestId?: string;
  dueDate: Date;
  pageSupportedLanguages?: string[];
  pageLanguage?: string;
  changePageLanguage?: React.Dispatch<React.SetStateAction<string>>;
}

interface ITalkProps extends IProps {
  type: "talk";
  status: TalkStatus;
  icName: string;
  dsvName: string;
}

interface IQuestionnaireProps extends IProps {
  type: "questionnaire";
  status: SubmissionStatus;
}

type Props = ITalkProps | IQuestionnaireProps;

const TalkHead = (props: Props) => {
  const {
    title,
    subTitle,
    template,
    dueDate,
    status,
    dataTestId,
    type,
    pageSupportedLanguages,
    pageLanguage,
    changePageLanguage
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <div data-qa-selector={dataTestId}>
      <Typography data-qa-selector="title" variant="h2">
        {title}
      </Typography>
      <Typography data-qa-selector="subTitle" variant="h3">
        {subTitle}
      </Typography>
      <ul className={classes.stats}>
        <li className={classes.statsItem}>
          <Typography variant="body1">
            {`${t(`feedback.talkHeader.template`)}: `}
            <span data-qa-selector="templateType">{template}</span>
          </Typography>
        </li>
        <li className={classes.statsItem}>
          <Typography variant="body1">
            {`${t(`feedback.talkHeader.dueDate`)}: `}
            <span data-qa-selector="templateDueDate">{displayDate(dueDate)}</span>
          </Typography>
        </li>
        <li className={classes.statsItem}>
          <Typography variant="body1" display="inline">
            {`${t(`feedback.talkHeader.status`)}: `}
          </Typography>
          {type === "talk" ? (
            <Status
              type="talk"
              icName={(props as ITalkProps).icName}
              dsvName={(props as ITalkProps).dsvName}
              status={status as TalkStatus}
            />
          ) : (
            <Status type="questionnaire" status={status as SubmissionStatus} />
          )}
        </li>

        {pageSupportedLanguages && changePageLanguage && (
          <li className={classes.statsItem}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="body1">{`${t(`feedback.talkHeader.language`)}: `}</Typography>
              <Select
                disabled={pageSupportedLanguages.length === 1}
                data-qa-selector="templateLanguage"
                value={pageLanguage}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                onChange={event => {
                  changePageLanguage(event.target.value as string);
                }}
              >
                {pageSupportedLanguages.map(language => (
                  <MenuItem key={language} value={language}>
                    <Typography variant="button">{language}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </li>
        )}
      </ul>
    </div>
  );
};

export default React.memo(TalkHead);
